import { supabase } from "./client";
import { withRequestDeadline } from "./requestDeadline";

const contractRpc = (name, args) => withRequestDeadline(
  (signal) => supabase.rpc(name, args).abortSignal(signal),
  { message: "La verificacion tardo demasiado. Reintenta con los mismos datos." }
);

const missingFunction = (error, name) =>
  error?.code === "PGRST202" && String(error.message || "").includes(name);

export async function getRestaurantRegistrationContract() {
  const { data, error } = await contractRpc("get_restaurant_registration_contract");
  // Compatibility before this migration is deployed; other failures must not bypass consent.
  if (missingFunction(error, "get_restaurant_registration_contract")) return { enabled: false };
  if (error) throw new Error("No pudimos cargar las condiciones de registro. Reintenta en unos momentos.");
  if (!data || typeof data.enabled !== "boolean" || (data.enabled && (
    data.document?.status !== "approved" || typeof data.document?.bodyText !== "string" || !data.document.bodyText || !data.document?.id ||
    !/^[a-f0-9]{64}$/.test(data.document?.contentSha256 || "")
  ))) throw new Error("Las condiciones de registro no estan disponibles. Contacta a soporte.");
  return data;
}

export async function registerRestaurantWithContract(payload) {
  const { contractAcceptance, ...registration } = payload;
  if (contractAcceptance?.accepted !== true || contractAcceptance?.authority !== true) {
    throw new Error("Acepta el contrato y confirma tu representacion del restaurante.");
  }
  const { data, error } = await contractRpc("register_restaurant_with_contract", {
    p_request_id: contractAcceptance.requestId,
    p_version_id: contractAcceptance.versionId,
    p_content_sha256: contractAcceptance.contentSha256,
    p_payload: registration,
    p_accepted: contractAcceptance.accepted === true,
    p_authority: contractAcceptance.authority === true,
  });
  if (error) throw new Error(error.message || "No pudimos registrar el contrato. No vuelvas a crear otra cuenta.");
  if (!data?.company?.id || !data?.branch?.id || !data?.acceptanceId) {
    throw new Error("No pudimos confirmar el alta. Reintenta con los mismos datos.");
  }
  return data;
}

export async function getRestaurantContractReceipts(companyId) {
  const { data, error } = await contractRpc("get_restaurant_contract_receipts", { p_company_id: companyId });
  if (missingFunction(error, "get_restaurant_contract_receipts")) return [];
  if (error) throw new Error("No pudimos cargar tu contrato. Reintenta en unos momentos.");
  if (!Array.isArray(data)) throw new Error("No pudimos verificar los contratos de esta cuenta.");
  return data;
}

export async function getRestaurantContractPdf(acceptanceId) {
  const { data, error } = await supabase.functions.invoke("restaurant-contract-delivery", {
    body: { action: "download", acceptanceId },
  });
  if (error || !(data instanceof Blob) || data.type !== "application/pdf") {
    throw new Error("No pudimos descargar la copia del contrato. Reintenta.");
  }
  return data;
}

export async function getRestaurantContractDeliveryAlerts() {
  const { data, error } = await contractRpc("list_restaurant_contract_delivery_alerts");
  if (error) throw new Error("No pudimos consultar los envios de contratos.");
  return data || [];
}

export async function retryRestaurantContractDelivery(acceptanceId) {
  const { error } = await contractRpc("retry_restaurant_contract_delivery", {
    p_acceptance_id: acceptanceId, p_confirmed_not_sent: true,
  });
  if (error) throw new Error(error.message || "No se pudo reprogramar el envio.");
}

export async function getRestaurantContractRequirement(companyId) {
  const name = "get_restaurant_contract_requirement";
  const { data, error } = await contractRpc(name, { p_company_id: companyId });
  if (missingFunction(error, name)) return { enabled: false, required: false, canAccept: false };
  if (error) throw new Error("No pudimos verificar el contrato de tu restaurante. Reintenta.");
  if (!data || ["enabled", "required", "canAccept"].some((key) => typeof data[key] !== "boolean") ||
    (!data.enabled && (data.required || data.canAccept)) || (data.canAccept && (!data.required ||
      data.document?.status !== "approved" || !data.document?.id || !data.document?.bodyText ||
      !/^[a-f0-9]{64}$/.test(data.document?.contentSha256 || "") ||
      !/^[a-f0-9]{64}$/.test(data.scopeSha256 || "") || !Array.isArray(data.scope?.branches)))) {
    throw new Error("No pudimos comprobar la version del contrato. Contacta a soporte.");
  }
  return data;
}

export async function acceptExistingRestaurantContract(companyId, acceptance) {
  if (acceptance.accepted !== true || acceptance.authority !== true) {
    throw new Error("Acepta el contrato y confirma tu representacion del restaurante.");
  }
  const { data, error } = await contractRpc("accept_existing_restaurant_contract", {
    p_company_id: companyId, p_request_id: acceptance.requestId, p_version_id: acceptance.versionId,
    p_content_sha256: acceptance.contentSha256, p_scope_sha256: acceptance.scopeSha256,
    p_party: acceptance.party, p_accepted: true, p_authority: true,
  });
  if (error) throw new Error(error.message || "No pudimos confirmar la aceptacion. Reintenta con los mismos datos.");
  if (!data?.acceptanceId || !Number.isFinite(Date.parse(data.acceptedAt))) {
    throw new Error("No pudimos confirmar la aceptacion. Reintenta con los mismos datos.");
  }
  return data;
}
